/**
 * SimHost — instancia o `core` no cliente e o avança a passo FIXO de 60 Hz.
 *
 * O acumulador desacopla o relógio do navegador (rAF, 30–240 Hz, variável) do
 * relógio da simulação (ticks inteiros): é o que faz o mesmo replay valer em
 * qualquer máquina. No solo esta instância é a autoridade; nos modos online
 * ela vira a cópia de predição (§13.6) sem mudar de forma.
 */

import {
  NEUTRAL_INPUT,
  ReplayRecorder,
  RULES_VERSION,
  Sim,
  TICK_MS,
  type Input,
  type Replay,
} from '@pixelraid/core'

/** Teto de ticks por frame: além disso, descarta atraso (aba em background). */
const MAX_CATCHUP_TICKS = 6

export class SimHost {
  readonly sim: Sim
  readonly seed: number
  readonly playerCount: number
  private readonly recorder = new ReplayRecorder()
  private accumulatorMs = 0

  constructor(seed: number, playerCount = 1, mode?: 'solo' | 'coopLocal' | 'coopOnline' | 'race') {
    this.seed = seed
    this.playerCount = playerCount
    this.sim = new Sim({
      world: { kind: 'procedural', seed },
      mode: mode ?? (playerCount > 1 ? 'coopLocal' : 'solo'),
      playerCount,
    })
  }

  /** Avança a simulação pelo tempo real decorrido. Retorna ticks executados. */
  update(deltaMs: number, inputs: readonly Input[]): number {
    this.accumulatorMs += deltaMs
    let steps = 0
    while (this.accumulatorMs >= TICK_MS && steps < MAX_CATCHUP_TICKS) {
      if (!this.sim.gameOver) {
        // Replay é moeda de ranking SOLO (§14); co-op local não pontua (§12.1).
        if (this.playerCount === 1) this.recorder.record(inputs[0] ?? NEUTRAL_INPUT)
        this.sim.step(inputs)
      }
      this.accumulatorMs -= TICK_MS
      steps++
    }
    // Aba dormiu? Joga o atraso fora em vez de simular uma rajada — a run é
    // do jogador, não do relógio.
    if (steps === MAX_CATCHUP_TICKS) this.accumulatorMs = 0
    return steps
  }

  /**
   * Guest de co-op online: avança UM tick com os inputs autoritativos.
   *
   * Não passa pelo `update()` de propósito. O guest não pode ser guiado por
   * relógio de parede: se ele avançasse sozinho, andaria à frente ou atrás do
   * host e os dois estariam jogando mundos diferentes. Quem dá o compasso é a
   * chegada dos inputs — o relógio do guest só decide QUANDO ele desenha, não
   * quando o mundo anda.
   */
  stepAuthoritative(inputs: readonly Input[]): void {
    if (this.sim.gameOver) return
    this.sim.step(inputs)
  }

  /** O replay da run corrente — a moeda do ranking de season (§14). */
  exportReplay(): Replay {
    return this.recorder.finish(
      {
        rulesVersion: RULES_VERSION,
        world: { kind: 'procedural', seed: this.seed },
        mode: 'solo',
        playerCount: 1,
        startTick: 0,
      },
      {
        score: this.sim.totalScore,
        distance: this.sim.scrollDist,
        checksum: this.sim.checksum(),
      },
    )
  }
}

export { NEUTRAL_INPUT }
